import type { EventCategoryId, EventId, EventPriorityId, EventStatusId } from "~domain/events/models/event-model.ts";
import type { OperatorId } from "~domain/operators/models/operator-model.ts";
import type { SiteId } from "~domain/sites/models/site-model.ts";

import { z } from "zod";

const backofficeEventCategorySchema = z.object({
  id: z.number().transform((s) => s as EventCategoryId),
  description: z.string(),
});

const backofficeOperatorSchema = z.object({
  id: z
    .number()
    .nullable()
    .transform((s) => s as OperatorId),
  firstname: z.string().nullable(),
  lastname: z.string().nullable(),
});

const backofficeEventSchema = z.object({
  closureDate: z.coerce.date().nullable(),
  createDateTime: z.coerce.date(),
  date: z.coerce.date(),
  description: z.string().nullable(),
  eventCategory: backofficeEventCategorySchema,
  forecastDate: z.coerce.date().nullable(),
  id: z.number().transform((s) => s as EventId),
  isOpen: z.boolean().nullable(),
  modificationDate: z.coerce.date(),
  operator: backofficeOperatorSchema,
  operatorDescription: z.string().nullable(),
  priority: z
    .object({
      id: z.number().transform((s) => s as EventPriorityId),
      description: z.string(),
    })
    .nullable(),
  status: z
    .object({
      id: z.number().transform((s) => s as EventStatusId),
      description: z.string(),
    })
    .nullable(),
  siteId: z.number().transform((s) => s as SiteId),
});

type BackofficeFindSiteEventTreeResponseItemDefinition = {
  data: z.infer<typeof backofficeEventSchema>;
  truncated: boolean;
  children: BackofficeFindSiteEventTreeResponseItemDefinition[];
};

export interface BackofficeFindSiteEventTreeResponseItemInput {
  data: z.input<typeof backofficeEventSchema>;
  truncated: z.input<z.ZodBoolean>;
  children: BackofficeFindSiteEventTreeResponseItemInput[];
}

export const backofficeFindSiteEventTreeResponseItem: z.ZodType<
  BackofficeFindSiteEventTreeResponseItemDefinition,
  z.ZodTypeDef,
  BackofficeFindSiteEventTreeResponseItemInput
> = z.object({
  data: backofficeEventSchema,
  truncated: z.boolean(),
  children: z.lazy(() => backofficeFindSiteEventTreeResponseItem.array()),
});

export const backofficeFindSiteEventTreeResponseSchema = backofficeFindSiteEventTreeResponseItem.array();

export type BackofficeFindSiteEventTreeResponseItem = z.infer<typeof backofficeFindSiteEventTreeResponseItem>;
export type BackofficeFindSiteEventTreeResponse = z.infer<typeof backofficeFindSiteEventTreeResponseSchema>;
